import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, Banknote, Check, Loader2, X } from 'lucide-react';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import ConfirmModal from '../shared/ConfirmModal';
import { SwitchRow } from '../settings/instantSettings';
import { agentSupportsCashDrawer } from './cashDrawer';
import { ApiResult, PrintMethod, PrintingAgent, PrintingPrinter, PRINT_METHODS, printingApi } from './printingApi';
import {
  BEEP_MIN_AGENT, DraftError, PrinterDraft, PrinterPrefill,
  createBody, draftFromPrinter, drawerTestBody, newDraft, updateBody, validateDraft, versionAtLeast,
} from './printingModel';
import { GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, SELECT_CLS } from './printingUi';

/**
 * Πλαϊνό παράθυρο εκτυπωτή (όπως η φόρμα υπαλλήλου): όνομα, ουρά Windows, τι τυπώνει (αποδείξεις /
 * δελτία), στυλ δελτίου, χαρτί, μέθοδος εκτύπωσης, συρτάρι (μόνο αποδείξεις), μπιπ (μόνο δελτία).
 * Χωρίς «Προχωρημένα» (απόφαση ιδιοκτήτη 25/10/2026): πακέτα/λωρίδα/παλμοί μένουν όπως είναι στη βάση.
 * Νέος: POST με τις προεπιλογές του συμβολαίου· υπάρχων: PUT μόνο με ό,τι άλλαξε.
 */

interface Props {
  storeId: number;
  /** null = νέος εκτυπωτής. */
  printer: PrintingPrinter | null;
  prefill?: PrinterPrefill;
  printers: PrintingPrinter[];
  agent: PrintingAgent;
  /** Ουρές από την «Ανακάλυψη» — προτάσεις στο πεδίο της ουράς. */
  queues: string[];
  onSaved: (name: string, created: boolean) => void;
  onClose: () => void;
}

type TestState = { state: 'idle' } | { state: 'busy' } | { state: 'done'; ok: boolean; message: string };

const LABEL_CLS = 'block text-sm font-medium text-t2 mb-1.5';
const HINT_CLS = 'mt-1.5 text-xs text-t3';
const ERROR_CLS = 'mt-1.5 text-xs text-red-600 dark:text-red-400';
const LEGEND_CLS = 'text-sm font-semibold text-t1 mb-2';

/** Επιλογή-«κουτί» (radio ή checkbox) με εγγενές input: βελάκια, Space και αναγνώστες οθόνης όπως πάντα. */
const Choice: React.FC<{
  type: 'radio' | 'checkbox';
  name?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  describedBy?: string;
}> = ({ type, name, checked, onChange, label, describedBy }) => (
  <label className="relative inline-flex">
    <input
      type={type}
      name={name}
      className="peer sr-only"
      checked={checked}
      aria-describedby={describedBy}
      onChange={e => onChange(e.target.checked)}
    />
    <span
      className={`inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-colors cursor-pointer
        peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1
        ${checked ? 'border-brand bg-brand-soft text-brand' : 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3'}`}
    >
      {type === 'checkbox' && (
        <span className={`grid place-items-center w-4 h-4 rounded border ${checked ? 'border-brand bg-brand text-brand-fg' : 'border-line-strong'}`} aria-hidden="true">
          {checked && <Check className="w-3 h-3" />}
        </span>
      )}
      {label}
    </span>
  </label>
);

const PrinterForm: React.FC<Props> = ({ storeId, printer, prefill, printers, agent, queues, onSaved, onClose }) => {
  const { t } = useTranslation(['printing']);
  const uid = useId();
  const isCreate = printer === null;
  const initial = useMemo(
    () => (printer ? draftFromPrinter(printer) : newDraft(printers, prefill)),
    // Η φόρμα ξεκινά μία φορά, με ό,τι ίσχυε όταν άνοιξε.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const form = useDirtyForm<PrinterDraft>(initial, { guardId: 'printing-printer' });
  const { values: v, setField } = form;
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [drawerTest, setDrawerTest] = useState<TestState>({ state: 'idle' });
  const [beepTest, setBeepTest] = useState<TestState>({ state: 'idle' });
  const nameRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<HTMLInputElement>(null);
  const rolesRef = useRef<HTMLDivElement>(null);

  const errors = validateDraft(v);
  const changes = isCreate ? null : updateBody(initial, v);
  const unsaved = isCreate ? form.dirty : Object.keys(changes || {}).length > 0;
  const shown = (key: DraftError) => errors.includes(key) && (attempted || touched[key] === true);

  const change = <K extends keyof PrinterDraft>(key: K, value: PrinterDraft[K]) => {
    setError(null);
    setField(key, value);
  };
  const touch = (key: string) => setTouched(prev => (prev[key] ? prev : { ...prev, [key]: true }));

  const requestClose = () => {
    if (saving) return;
    if (unsaved) setConfirmDiscard(true);
    else onClose();
  };

  // Esc κλείνει (με ερώτηση αν υπάρχουν αλλαγές)· πρώτα το παράθυρο επιβεβαίωσης.
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirmDiscard) setConfirmDiscard(false);
      else closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmDiscard]);

  const failure = (r: ApiResult<unknown>, fallback: string): string => {
    if (r.status === 0) return t('printing:form.network');
    if (r.status === 403) return r.error || t('printing:form.forbidden');
    return r.error || fallback;
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setAttempted(true);
    if (errors.length > 0) {
      const first = errors[0];
      if (first === 'name') nameRef.current?.focus();
      else if (first === 'windows_printer_name') queueRef.current?.focus();
      else rolesRef.current?.querySelector<HTMLInputElement>('input')?.focus();
      return;
    }
    if (saving || (!isCreate && !unsaved)) return;
    setSaving(true);
    setError(null);
    const r = isCreate
      ? await printingApi.createPrinter(storeId, createBody(v, printers))
      : await printingApi.updatePrinter(storeId, printer!.id, changes || {});
    if (!r.ok) {
      setSaving(false);
      setError(failure(r, t('printing:form.saveFailed')));
      return;
    }
    form.markSaved();
    onSaved(v.name.trim(), isCreate);
  };

  const runDrawerTest = async () => {
    setDrawerTest({ state: 'busy' });
    const r = await printingApi.testDrawer(storeId, printer!.id, drawerTestBody(v));
    if (r.ok) setDrawerTest({ state: 'done', ok: true, message: t('printing:form.drawerTestSent') });
    else if (r.status === 404 && !r.error) setDrawerTest({ state: 'done', ok: false, message: t('printing:form.drawerTestUnsupported') });
    else setDrawerTest({ state: 'done', ok: false, message: failure(r, t('printing:form.drawerTestFailed')) });
  };

  const runBeepTest = async () => {
    setBeepTest({ state: 'busy' });
    const r = await printingApi.testBeep(storeId, printer!.id, { beep_enabled: v.beep_enabled });
    if (r.ok) setBeepTest({ state: 'done', ok: true, message: t('printing:form.beepTestSent') });
    else if (r.status === 404 && !r.error) setBeepTest({ state: 'done', ok: false, message: t('printing:form.beepTestUnsupported') });
    else setBeepTest({ state: 'done', ok: false, message: failure(r, t('printing:form.beepTestFailed')) });
  };

  // Η δήλωση του agent (caps) πρώτα, μετά η έκδοση — ίδιος κανόνας με τις δοκιμές του server.
  const drawerOutdated = agent.connected && !agentSupportsCashDrawer(agent.version, agent.caps);
  const beepOutdated = agent.connected && !agent.caps.includes('beep') && !versionAtLeast(agent.version, BEEP_MIN_AGENT);
  const agentVersion = agent.version || t('printing:agent.versionUnknownShort');

  const testLine = (state: TestState) => (state.state === 'done' ? (
    <p role="status" className={`text-xs ${state.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
      {state.message}
    </p>
  ) : null);

  const titleId = `${uid}-title`;
  const queueList = queues.filter(q => q.trim() !== '');

  return (
    <div
      className="role-overlay fixed inset-0 z-50 flex justify-end"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="role-dialog h-full w-full sm:max-w-xl bg-surface-1 border-l border-line shadow-2xl flex flex-col"
        data-testid="printer-form"
      >
        <header className="role-dialog-header flex items-start justify-between gap-3 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-t1">
              {isCreate ? t('printing:form.addTitle') : t('printing:form.editTitle')}
            </h2>
            {printer && <p className="text-sm text-t3 truncate">{printer.name}</p>}
          </div>
          <button type="button" onClick={requestClose} aria-label={t('printing:form.close')}
            className="w-10 h-10 shrink-0 rounded-xl text-t3 hover:text-t1 hover:bg-surface-3 flex items-center justify-center">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </header>

        <form id={`${uid}-form`} onSubmit={submit} noValidate className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${uid}-name`} className={LABEL_CLS}>{t('printing:form.name')}</label>
              <input id={`${uid}-name`} ref={nameRef} autoFocus value={v.name} maxLength={100} autoComplete="off"
                placeholder={t('printing:form.namePlaceholder')}
                onChange={e => change('name', e.target.value)} onBlur={() => touch('name')}
                aria-invalid={shown('name') || undefined}
                aria-describedby={shown('name') ? `${uid}-name-error` : undefined}
                className={INPUT_CLS} />
              {shown('name') && <p id={`${uid}-name-error`} className={ERROR_CLS}>{t('printing:form.nameRequired')}</p>}
            </div>
            <div>
              <label htmlFor={`${uid}-queue`} className={LABEL_CLS}>{t('printing:form.queue')}</label>
              <input id={`${uid}-queue`} ref={queueRef} value={v.windows_printer_name} maxLength={255} autoComplete="off"
                spellCheck={false} list={queueList.length > 0 ? `${uid}-queues` : undefined}
                onChange={e => change('windows_printer_name', e.target.value)} onBlur={() => touch('windows_printer_name')}
                aria-invalid={shown('windows_printer_name') || undefined}
                aria-describedby={`${uid}-queue-hint`}
                className={`${INPUT_CLS} font-mono`} />
              {queueList.length > 0 && (
                <datalist id={`${uid}-queues`}>{queueList.map(q => <option key={q} value={q} />)}</datalist>
              )}
              <p id={`${uid}-queue-hint`} className={shown('windows_printer_name') ? ERROR_CLS : HINT_CLS}>
                {shown('windows_printer_name') ? t('printing:form.queueRequired') : t('printing:form.queueHint')}
              </p>
            </div>
          </div>

          <fieldset>
            <legend className={LEGEND_CLS}>{t('printing:form.roles')}</legend>
            <div ref={rolesRef} className="flex flex-wrap gap-2">
              <Choice type="checkbox" checked={v.prints_receipts} onChange={c => change('prints_receipts', c)}
                label={t('printing:roles.receipts')} describedBy={`${uid}-roles-hint`} />
              <Choice type="checkbox" checked={v.prints_tickets} onChange={c => change('prints_tickets', c)}
                label={t('printing:roles.tickets')} describedBy={`${uid}-roles-hint`} />
            </div>
            <p id={`${uid}-roles-hint`} className={shown('roles') ? ERROR_CLS : HINT_CLS}>
              {shown('roles') ? t('printing:form.rolesRequired') : t('printing:form.rolesHint')}
            </p>
          </fieldset>

          {v.prints_tickets && (
            <fieldset>
              <legend className={LEGEND_CLS}>{t('printing:form.style')}</legend>
              <div className="flex flex-wrap gap-2">
                <Choice type="radio" name={`${uid}-style`} checked={v.ticket_style === 'kitchen'}
                  onChange={() => change('ticket_style', 'kitchen')} label={t('printing:styles.kitchen')} describedBy={`${uid}-style-hint`} />
                <Choice type="radio" name={`${uid}-style`} checked={v.ticket_style === 'bar'}
                  onChange={() => change('ticket_style', 'bar')} label={t('printing:styles.bar')} describedBy={`${uid}-style-hint`} />
              </div>
              <p id={`${uid}-style-hint`} className={HINT_CLS}>
                {v.ticket_style === 'bar' ? t('printing:form.styleBarHint') : t('printing:form.styleKitchenHint')}
              </p>
            </fieldset>
          )}

          <fieldset>
            <legend className={LEGEND_CLS}>{t('printing:form.paper')}</legend>
            <div className="flex flex-wrap gap-2">
              {([80, 58] as const).map(w => (
                <Choice key={w} type="radio" name={`${uid}-paper`} checked={v.paper_width === w}
                  onChange={() => change('paper_width', w)} label={t('printing:paper', { width: w })} />
              ))}
            </div>
          </fieldset>

          <div data-testid="printer-method">
            <label htmlFor={`${uid}-method`} className={LABEL_CLS}>{t('printing:form.method')}</label>
            <select id={`${uid}-method`} value={v.print_method} className={SELECT_CLS}
              aria-describedby={`${uid}-method-hint`}
              onChange={e => change('print_method', e.target.value as PrintMethod)}>
              {PRINT_METHODS.map(m => <option key={m} value={m}>{t(`printing:methods.${m}`)}</option>)}
            </select>
            {v.print_method === 'hybrid' && (
              <div className="mt-2"><Notice>{t('printing:form.methodHybridWarning')}</Notice></div>
            )}
            <p id={`${uid}-method-hint`} className={HINT_CLS}>
              {v.print_method === 'text' ? t('printing:form.methodTextHint') : t('printing:form.methodHint')}
            </p>
          </div>

          {(v.prints_receipts || v.prints_tickets) && (
            <div className="space-y-4">
              {v.prints_receipts && (
                <div data-testid="drawer-block">
                  <SwitchRow
                    title={t('printing:form.drawer')}
                    subtitle={t('printing:form.drawerSub')}
                    note={t('printing:form.drawerNote')}
                    checked={v.drawer_enabled}
                    onChange={c => change('drawer_enabled', c)}
                  />
                  {v.drawer_enabled && drawerOutdated && (
                    <div className="mt-3"><Notice>{t('printing:form.drawerOutdated', { version: agentVersion })}</Notice></div>
                  )}
                  {v.drawer_enabled && !isCreate && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                      <button type="button" onClick={runDrawerTest} disabled={drawerTest.state === 'busy'} className={GHOST_BTN}>
                        {drawerTest.state === 'busy'
                          ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                          : <Banknote className="w-4 h-4" aria-hidden="true" />}
                        {t('printing:form.drawerTest')}
                      </button>
                      <span className="text-xs text-t3">{t('printing:form.drawerTestHint')}</span>
                      {testLine(drawerTest)}
                    </div>
                  )}
                </div>
              )}
              {v.prints_tickets && (
                <div data-testid="beep-block" className={v.prints_receipts ? 'pt-4 border-t border-line' : ''}>
                  <SwitchRow
                    title={t('printing:form.beep')}
                    subtitle={t('printing:form.beepSub')}
                    note={t('printing:form.beepNote')}
                    checked={v.beep_enabled}
                    onChange={c => change('beep_enabled', c)}
                  />
                  {v.beep_enabled && beepOutdated && (
                    <div className="mt-3"><Notice>{t('printing:form.beepOutdated', { version: agentVersion })}</Notice></div>
                  )}
                  {v.beep_enabled && !isCreate && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                      <button type="button" onClick={runBeepTest} disabled={beepTest.state === 'busy'} className={GHOST_BTN}>
                        {beepTest.state === 'busy'
                          ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                          : <BellRing className="w-4 h-4" aria-hidden="true" />}
                        {t('printing:form.beepTest')}
                      </button>
                      <span className="text-xs text-t3">{t('printing:form.beepTestHint')}</span>
                      {testLine(beepTest)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </form>

        <footer className="border-t border-line px-5 py-4 bg-surface-1">
          {error ? (
            <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400 mb-3">{error}</p>
          ) : (
            <p role="status" className="text-xs text-t3 mb-3">
              {isCreate ? t('printing:form.createHint') : unsaved ? t('printing:form.unsaved') : t('printing:form.noChanges')}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={requestClose} disabled={saving} className={GHOST_BTN}>
              {t('printing:cancel')}
            </button>
            <button type="submit" form={`${uid}-form`} disabled={saving || (!isCreate && !unsaved)} aria-busy={saving} className={PRIMARY_BTN}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {saving ? t('printing:form.saving') : isCreate ? t('printing:form.create') : t('printing:form.save')}
            </button>
          </div>
        </footer>
      </div>

      <ConfirmModal
        isOpen={confirmDiscard}
        title={t('printing:form.discardTitle')}
        message={t('printing:form.discardMessage')}
        confirmLabel={t('printing:form.discardConfirm')}
        cancelLabel={t('printing:form.discardCancel')}
        variant="warning"
        onConfirm={() => { setConfirmDiscard(false); form.reset(); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
};

export default PrinterForm;
